import React, { useEffect, useMemo, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import {
  CreateUpdateEmailAddressDto,
  GetClientDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNotifications } from '../../../common/hooks/useNotifications';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { usePokCore } from '../../../common/hooks/usePokCore';
import FormRow from '../../../app/components/FormRow';
import Button from '../../../common/components/Button/Button';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { ClientSelector } from '../Selects/ClientSelector';
import { Option } from '../Selects/Selector';
import DynamicTextarea from '../../../app/components/DynamicTextarea';
import DictionarySelector from '../Selects/DictionarySelector';

interface EmailAddressEditorProps {
  id?: string;
  emailAddress: CreateUpdateEmailAddressDto;
  handleClose?: () => void;
  show?: boolean;
  client?: GetClientDto;
  onCompleted: (
    emailAddress: CreateUpdateEmailAddressDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const EmailAddressEditor: React.FC<EmailAddressEditorProps> = props => {
  const [emailAddress, setEmailAddress] = useState(props.emailAddress);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [defaultEmailFrom, setDefaultEmailFrom] = useState<string>();

  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('clientLng');

  useMemo(() => {
    pok.emailAddresses
      .gGetDefaultEmailFrom()
      .then(res => {
        setDefaultEmailFrom(res.id);
      })
      .catch(err => {
        notifications.caughtError(err);
      });
  }, [notifications, pok.emailAddresses]);

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setEmailAddress(props.emailAddress);
    }
  }, [props.emailAddress, props.show]);

  useEffect(() => {
    if (!props.id && defaultEmailFrom && !emailAddress.emailFromId) {
      setEmailAddress(prev => ({ ...prev, emailFromId: defaultEmailFrom }));
    }
  }, [defaultEmailFrom, props.id, emailAddress]);

  const createEmailAddress = async () => {
    setIsLoading(true);
    props
      .onCompleted(emailAddress, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
        notifications.saveCompleted();
      })
      .catch(async errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setIsLoading(false));
  };

  const propertyChange = (obj: Partial<CreateUpdateEmailAddressDto>) => {
    setEmailAddress({ ...emailAddress, ...obj });
  };

  return (
    <>
      <Modal
        onHide={props.handleClose}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {props.id ? t(tk.emailAddress.edit) : t(tk.emailAddress.add)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="d-grid gap-4 mb-3">
            <FormRow controlId="client" label={t(tk.client.client)}>
              <ClientSelector
                onChange={e => {
                  if (propertyChange) {
                    propertyChange({
                      clientId: (e as Option).value,
                    });
                  }
                }}
                value={props?.client}
                readOnly={true}
              />
            </FormRow>
            <FormRow controlId="emailsTo" label={t(tk.emailAddress.emailsTo)}>
              <DynamicTextarea
                minRows={2}
                value={emailAddress.emailsTo || ''}
                onChange={e => propertyChange({ emailsTo: e.target.value })}
              />
            </FormRow>
            <FormRow controlId="emailsDw" label={t(tk.emailAddress.emailsDw)}>
              <DynamicTextarea
                minRows={2}
                value={emailAddress.emailsDw || ''}
                onChange={e => propertyChange({ emailsDw: e.target.value })}
              />
            </FormRow>
            <FormRow controlId="emailFrom" label={t(tk.emailAddress.emailFrom)}>
              <DictionarySelector
                dictionary="Adresy nadawcze dla faktur skonfigurowane w Gmail"
                value={emailAddress.emailFromId || defaultEmailFrom}
                onChange={option =>
                  propertyChange({
                    emailFromId: option.value,
                  })
                }
              />
            </FormRow>
            <FormRow controlId="comment" label={t(tk.emailAddress.comment)}>
              <DynamicTextarea
                minRows={3}
                value={emailAddress.comment || ''}
                onChange={e => propertyChange({ comment: e.target.value })}
              />
            </FormRow>
            <FormRow controlId="active" label={t(tk.common.activeHe)}>
              <Form.Check
                type="checkbox"
                className="pt-2"
                checked={emailAddress.active ?? true}
                onChange={e => propertyChange({ active: e.target.checked })}
              />
            </FormRow>
          </Form>
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="m-3"
          />
        </Modal.Body>
        <Modal.Footer>
          {props.id && (
            <LastEditorInfo
              id={props.id}
              method={pok.emailAddresses.getLastEditor}
            />
          )}
          <Button
            variant="outline-secondary"
            onClick={props.handleClose}
            disabled={isLoading}
          >
            {t(tk.button.close)}
          </Button>
          <Button
            variant="primary"
            onClick={createEmailAddress}
            isLoading={isLoading}
          >
            {t(tk.button.save)}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
